import type { AccountInfo } from "@azure/msal-browser";
import { authLogger } from "./auth-logger";
import { type UserInfo } from "@/types/core";
import { extractName } from "./app-utils";

/**
 * Client-side claim extraction helpers.
 * Authorization and token authenticity are enforced exclusively by the API.
 */
export class TokenClaimExtractor {
  private static instance: TokenClaimExtractor;

  static getInstance(): TokenClaimExtractor {
    if (!TokenClaimExtractor.instance) {
      TokenClaimExtractor.instance = new TokenClaimExtractor();
    }
    return TokenClaimExtractor.instance;
  }

  decodeToken(token: string): Record<string, unknown> | null {
    try {
      const parts = token.split(".");
      if (parts.length !== 3) {
        return null;
      }
      const payload = parts[1];
      const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
      return JSON.parse(decoded);
    } catch (error) {
      authLogger.error("Failed to decode token", { error: String(error) });
      return null;
    }
  }

  extractBasicUserInfo(account: AccountInfo): UserInfo {
    return {
      userId: account.localAccountId || "",
      name: account.name || "",
      email: account.username || ""
    };
  }

  extractUserInfoWithAccessToken(
    account: AccountInfo,
    accessToken?: string
  ): UserInfo {
    try {
      let userInfo = this.extractBasicUserInfo(account);

      if (
        accessToken &&
        (!userInfo.email || userInfo.email === account.username)
      ) {
        const accessTokenInfo =
          this.extractUserInfoFromAccessToken(accessToken);

        if (
          accessTokenInfo.email &&
          accessTokenInfo.email !== account.username
        ) {
          userInfo = {
            ...userInfo,
            userId: accessTokenInfo.userId || userInfo.userId,
            email: accessTokenInfo.email,
            name: accessTokenInfo.name || userInfo.name,
            firstName: accessTokenInfo.firstName || userInfo.firstName,
            middleName: accessTokenInfo.middleName || userInfo.middleName,
            lastName: accessTokenInfo.lastName || userInfo.lastName
          };
        }
      }

      return userInfo;
    } catch {
      return this.extractBasicUserInfo(account);
    }
  }

  /**
   * True when the access token carries Entra `roles` or `groups` claims.
   * After registration those claims are missing on the sign-in cached AT
   * until a force-refresh issues a new token.
   */
  hasAuthorizationClaims(accessToken: string): boolean {
    const claims = this.decodeToken(accessToken);
    if (!claims) {
      return false;
    }

    return (
      hasNonEmptyClaim(claims.roles) ||
      hasNonEmptyClaim(claims.groups) ||
      hasNonEmptyClaim(
        claims["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"]
      )
    );
  }

  extractUserInfoFromAccessToken(accessToken: string): UserInfo {
    try {
      const claims = this.decodeToken(accessToken);
      if (!claims) {
        return { userId: "", name: "", email: "" };
      }
      const _name = (claims.name as string) || "";
      const extractedName = extractName(_name);
      const preferredUsername = claims.preferred_username as string | undefined;
      return {
        userId: (claims.oid as string) || (claims.sub as string) || "",
        email:
          (claims.email as string) ||
          (preferredUsername?.includes("@") ? preferredUsername : "") ||
          "",
        name: _name,
        firstName: extractedName.firstName,
        middleName: extractedName.middleName,
        lastName: extractedName.lastName
      };
    } catch {
      return { userId: "", name: "", email: "" };
    }
  }
}

export const tokenClaimExtractor = TokenClaimExtractor.getInstance();

function hasNonEmptyClaim(value: unknown): boolean {
  if (Array.isArray(value)) {
    return value.some(
      (entry) => typeof entry === "string" && entry.trim().length > 0
    );
  }

  return typeof value === "string" && value.trim().length > 0;
}
